import React, { useState } from 'react';
import classNames from 'classnames';
import { Grid, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import { CardContainer, TitleText, ResponseRateText, RectangleText, getAttributeBasedTitle } from './_shared';
import { DEFAULT_RESULTS_TRANSLATION, COLORS } from '../../constants/results';

const useStyles = makeStyles(() => ({
  text: {
    color: COLORS.MAIN,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    '@supports (-webkit-line-clamp: 3)': {
      whiteSpace: 'initial',
      display: '-webkit-box',
      '-webkit-line-clamp': 3,
      '-webkit-box-orient': 'vertical',
    },
  },
  percent: {
    color: 'rgb(154, 154, 154)',
    paddingTop: 6,
  },
  option: {
    color: '#0F7B85',
  },
  numbers: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-around',
    width: '100%',
    paddingTop: 16,
    borderTop: '1px solid #E0E0E0',
  },
  comment: {
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: 14,
    color: '#14A5B2',
    border: '1px solid #14A5B2',
    borderRadius: 4,
    marginTop: 16,
    display: 'flex',
    justifyContent: 'center',
    padding: '4px 0',
  },
}));

const getMissingOptions = (options, choices) => {
  if (!options || !choices) return [];
  return choices.map((choice) => {
    const { id, language } = choice;
    const text = language.message;
    const matchedOption = options.find((option) => option.id === choice.id);
    if (!matchedOption) {
      return {
        id,
        text,
        numberOfComments: 0,
        percentage: 0,
        totalAnswered: 0,
      };
    }
    const { numberOfComments, percentage, totalAnswered } = matchedOption;
    return {
      id,
      text,
      numberOfComments,
      percentage,
      totalAnswered,
    };
  });
};

export default function OptionCard({
  title,
  isAttributeBased,
  questionAttributes,
  result,
  choices,
  handleCardClick,
  handleCommentClick,
  canViewComments,
  attributes = [],
  showHoverover = true,
  translation = DEFAULT_RESULTS_TRANSLATION,
}) {
  const classes = useStyles();
  const { t } = useTranslation();

  const [focused, setFocused] = useState(false);

  const { numberOfComments, totalAnswered, totalRespondents, options } = result;

  const completeOptions = getMissingOptions(options, choices);

  const attributesTitle = getAttributeBasedTitle(isAttributeBased, questionAttributes, attributes);

  const renderHeader = () => (
    <>
      <TitleText
        translation={translation}
        title={title}
        isAttributeBased={isAttributeBased}
        attributesTitle={attributesTitle}
      />
      <Grid container>
        {completeOptions.map((option) => {
          const percent = (option.percentage * 100).toFixed(1);
          const numberOfResponses = focused && showHoverover ? option.totalAnswered : null;
          return (
            <Grid item key={option.id} xs={12} style={{ padding: '6px 0px' }}>
              <Grid container>
                <Grid item xs={4} tabIndex={0}>
                  <Tooltip title={option.text}>
                    <Typography
                      className={classNames(classes.text, classes.option)}
                      aria-label={t('screenReader.option', { option: option.text })}
                    >
                      {option.text}
                    </Typography>
                  </Tooltip>
                </Grid>

                <Grid item xs={6} style={{ height: 30 }}>
                  <RectangleText percent={percent} numberOfResponses={numberOfResponses} />
                </Grid>

                <Grid item xs={2} container justifyContent="center" tabIndex={0}>
                  <Typography className={classes.percent} aria-label={t('screenReader.peopleSelected', { percent })}>
                    {`${percent}%`}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          );
        })}
      </Grid>
    </>
  );

  return (
    <CardContainer
      renderHeader={renderHeader}
      onClick={handleCardClick && handleCardClick(result)}
      translation={translation}
      onMouseEnter={() => setFocused(true)}
      onMouseLeave={() => setFocused(false)}
    >
      {(totalAnswered || totalRespondents) && (
        <Grid className={classes.numbers}>
          <ResponseRateText
            translation={translation}
            totalAnswered={totalAnswered}
            totalRespondents={totalRespondents}
            attributesTitle={attributesTitle}
          />
        </Grid>
      )}

      {canViewComments && numberOfComments > 0 && (
        <Grid container className={classes.comment} onClick={handleCommentClick && handleCommentClick(result)}>
          {`${translation.see} ${numberOfComments} ${translation.comments}`}
        </Grid>
      )}
    </CardContainer>
  );
}
